import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, BookOpenIcon } from 'lucide-react';
interface BookPreviewProps {
  isOpen: boolean;
  onClose: () => void;
}
export function BookPreview({
  isOpen,
  onClose
}: BookPreviewProps) {
  return <AnimatePresence data-id="element-47">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="fixed inset-0 bg-deep-brown/60 backdrop-blur-sm z-50" onClick={onClose} data-id="element-48" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        y: 40,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 40,
        scale: 0.97
      }} transition={{
        duration: 0.4,
        ease: [0.21, 0.47, 0.32, 0.98]
      }} className="fixed inset-4 md:inset-8 lg:inset-16 z-50 flex items-center justify-center" data-id="element-49">
            <div className="bg-cream rounded-3xl shadow-2xl w-full max-w-3xl max-h-full flex flex-col overflow-hidden border border-amber/20" data-id="element-50">
              {/* Header */}
              <div className="flex items-center justify-between px-8 py-6 border-b border-amber/15 bg-warm-beige/50 flex-shrink-0" data-id="element-51">
                <div className="flex items-center gap-3" data-id="element-52">
                  <div className="w-10 h-10 bg-terracotta/10 rounded-full flex items-center justify-center" data-id="element-53">
                    <BookOpenIcon className="w-5 h-5 text-terracotta" data-id="element-54" />
                  </div>
                  <div data-id="element-55">
                    <h3 className="text-xl font-bold text-deep-brown font-playfair" data-id="element-56">
                      Rompiendo el Silencio
                    </h3>
                    <p className="text-sm text-light-brown" data-id="element-57">
                      Extracto del libro
                    </p>
                  </div>
                </div>
                <button onClick={onClose} className="w-10 h-10 rounded-full bg-white/80 flex items-center justify-center text-medium-brown hover:bg-white hover:text-deep-brown transition-colors" aria-label="Cerrar" data-id="element-58">
                  <XIcon className="w-5 h-5" data-id="element-59" />
                </button>
              </div>

              {/* Content */}
              <div className="overflow-y-auto px-8 md:px-12 py-8 flex-1" data-id="element-60">
                <h2 className="text-2xl md:text-3xl font-bold text-deep-brown mb-8 font-playfair leading-tight" data-id="element-61">
                  Rompiendo el Silencio:{' '}
                  <span className="text-terracotta italic" data-id="element-62">
                    Un Relato de Sanación y Transformación desde el Abuso
                    Infantil
                  </span>
                </h2>

                <div className="space-y-6 text-medium-brown text-lg leading-relaxed font-inter" data-id="element-63">
                  <p data-id="element-64">
                    En{' '}
                    <strong className="text-deep-brown" data-id="element-65">
                      Rompiendo el Silencio
                    </strong>
                    , comparto de manera cruda y honesta mi travesía desde las
                    sombras del abuso infantil hacia la luz de la sanación. Este
                    relato íntimo y revelador te sumergirá en mi mundo marcado
                    por la violencia y la vulnerabilidad de la infancia.
                  </p>

                  <p data-id="element-66">
                    El abuso infantil se erige como el oscuro protagonista de mi
                    narrativa, tejiendo una trama de dolor, miedo y trauma que
                    dejó huellas imborrables en mi historia. A través de cada
                    página, la palabra "abuso infantil" resuena como un grito de
                    denuncia y un llamado urgente a la conciencia colectiva.
                  </p>

                  <p data-id="element-67">
                    Desde las cicatrices invisibles hasta las heridas
                    emocionales que perduran en el tiempo, este relato destapa
                    las capas de sufrimiento que acompañan a quienes han sido
                    víctimas de abuso infantil. Exploro con valentía y
                    determinación los efectos devastadores de este flagelo en la
                    psique y el espíritu de un niño inocente.
                  </p>

                  <div className="border-l-4 border-terracotta/40 pl-6 py-2 my-8 bg-terracotta/5 rounded-r-xl" data-id="element-68">
                    <p className="italic text-deep-brown" data-id="element-69">
                      A lo largo de mi viaje de sanación, aprendí que romper el
                      silencio es el primer paso hacia la liberación y el
                      empoderamiento. Cada confesión, cada recuerdo compartido,
                      opera como un acto de valentía y resistencia contra la
                      injusticia del abuso infantil.
                    </p>
                  </div>

                  <p data-id="element-70">
                    En este relato, el dolor se transforma en fuerza, la
                    vulnerabilidad en poder y la esperanza brilla como una luz
                    en la oscuridad.
                  </p>

                  <p data-id="element-71">
                    <strong className="text-deep-brown" data-id="element-72">
                      Rompiendo el Silencio
                    </strong>{' '}
                    no solo es un relato autobiográfico, sino también un alegato
                    en favor de la verdad, la compasión y la empatía. A través
                    de mi voz, busco inspirar a otros a alzar la suya, a
                    reclamar su espacio de dignidad y a sanar las heridas del
                    pasado. Juntos, podemos ser agentes de cambio en la lucha
                    contra el abuso infantil.
                  </p>

                  <p data-id="element-73">
                    Adéntrate en estas páginas con el coraje de enfrentar la
                    verdad y la humildad de abrir el corazón a la compasión.
                    Descubre en cada línea un testimonio de resiliencia, de amor
                    propio y de redención. Porque solo al romper el silencio,
                    podemos iniciar el camino hacia la sanación, la
                    transformación y la esperanza de un futuro libre de abuso
                    infantil.
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="px-8 py-6 border-t border-amber/15 bg-warm-beige/50 flex-shrink-0 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-74">
                <p className="text-sm text-light-brown italic text-center sm:text-left" data-id="element-75">
                  — Daniela Garcia Lantigua
                </p>
                <a href="https://7kaxyn4bz2pgw2pi-97302217014.shopifypreview.com/products_preview?preview_key=7423b83fb7c6725a7ae81fffa389df5e" target="_blank" rel="noopener noreferrer" className="bg-terracotta text-white px-8 py-3 rounded-full font-semibold shadow-warm hover:bg-terracotta-dark transition-colors duration-300" data-id="element-76">
                  Comprar el libro completo
                </a>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}